import React, { useState, useEffect } from "react";
import { Row, Col, Spin, Card, Table, Input, Modal } from "antd";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import MainLayout from "../../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../../components/layouts/enhancement/TopContent";
import cnt from "../../../../services/definedConstant";
import { rightAction } from "../../../../redux/actions";
import { labelConfig, staticConfig, apiConfig } from "../../treasuryContractBooking.config";
import { BOM } from "../../../../services/definedMethod";

const ds = { height: 44, background: "#F9FAFB", cursor: "not-allowed" };
const fmt = (v) => (v != null ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "—");

const BondContractTransactions = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [selectedTx, setSelectedTx] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [txLoading, setTxLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const contractId = location.state;
  const { userData } = props;
  const { session } = userData || {};

  useEffect(() => {
    if (contractId) fetchTransactions();
  }, [contractId]);

  const fetchTransactions = () => {
    setIsLoading(true);
    const body = { session };
    const url = `${apiConfig.GetBondContracts}/${contractId}/transactions`;

    BOM.FetchReqAction(body, url, (err, res) => {
      setIsLoading(false);
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err?.defaultUserMessage || "Failed to load transactions.");
        return;
      }

      const items = Array.isArray(res)
        ? res
        : Array.isArray(res?.pageItems)
          ? res.pageItems
          : [];

      setTransactions(items);
    });
  };

  const fetchTransactionDetail = (transactionId) => {
    setTxLoading(true);
    setModalOpen(true);
    const body = { session };
    const url = `${apiConfig.GetBondContracts}/${contractId}/transactions/${transactionId}`;

    BOM.FetchReqAction(body, url, (err, res) => {
      setTxLoading(false);
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err?.defaultUserMessage || "Failed to load transaction detail.");
        setModalOpen(false);
        return;
      }
      setSelectedTx(res);
    });
  };

  const columns = [
    {
      title: "Transaction ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Type",
      dataIndex: ["transactionType", "text"],
      key: "type",
      render: (v) => v ?? "—",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (v) => {
        if (!v) return "—";
        if (Array.isArray(v)) {
          const [y, m, d] = v;
          return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        }
        return v;
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (v) => fmt(v),
      align: "right",
    },
    {
      title: "Currency",
      dataIndex: ["currency", "code"],
      key: "currency",
      render: (v) => v ?? "—",
    },
    {
      title: "",
      key: "action",
      render: (_, record) => (
        <button
          type="button"
          onClick={() => fetchTransactionDetail(record.id)}
          style={{
            height: 34, padding: "0 16px", borderRadius: 8,
            border: "1px solid #BA24D5", background: "#fff",
            color: "#BA24D5", fontWeight: 600, fontSize: 13, cursor: "pointer",
          }}
        >
          View
        </button>
      ),
    },
  ];

  return (
    <MainLayout pageItems={staticConfig.manageBondContract}>
      <TopContent pageTitle={labelConfig.bondContractListing} />

      <Spin spinning={isLoading} size="large">
        <div style={{ padding: "24px" }}>
          <Card style={{ boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)", borderRadius: 20 }}>

            {/* Header */}
            <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
              <Col>
                <h3 style={{ margin: 0, fontWeight: 600 }}>Contract Transactions</h3>
                <p style={{ color: "#667085", fontSize: 14, margin: 0 }}>
                  All transactions for bond contract #{contractId}
                </p>
              </Col>
              <Col>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  style={{
                    height: 44, padding: "0 24px", borderRadius: 8,
                    border: "1px solid #D0D5DD", background: "#fff",
                    color: "#344054", fontWeight: 600, fontSize: 14, cursor: "pointer",
                  }}
                >
                  Back
                </button>
              </Col>
            </Row>

            <Table
              columns={columns}
              dataSource={transactions}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: "No transactions found for this contract." }}
            />
          </Card>
        </div>
      </Spin>

      {/* Transaction Detail Modal */}
      <Modal
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setSelectedTx(null); }}
        footer={null}
        title="Transaction Detail"
        width={600}
      >
        <Spin spinning={txLoading}>
          {selectedTx ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                ["Transaction ID", selectedTx.id],
                ["Type", selectedTx.transactionType?.text ?? "—"],
                ["Date", (() => {
                  const v = selectedTx.date;
                  if (!v) return "—";
                  if (Array.isArray(v)) {
                    const [y, m, d] = v;
                    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                  }
                  return v;
                })()],
                ["Amount", fmt(selectedTx.amount)],
                ["Currency", selectedTx.currency?.code ?? "—"],
                ["Note", selectedTx.note ?? "—"],
              ].map(([label, value]) => (
                <Row key={label} gutter={16}>
                  <Col span={10}>
                    <span style={{ fontWeight: 600, color: "#344054", fontSize: 13 }}>
                      {label}
                    </span>
                  </Col>
                  <Col span={14}>
                    <span style={{ color: "#667085", fontSize: 13 }}>{value}</span>
                  </Col>
                </Row>
              ))}
            </div>
          ) : (
            !txLoading && <p style={{ color: "#667085" }}>No detail available.</p>
          )}
        </Spin>
      </Modal>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  rightBtn: state.RightSideBtn,
  userData: state.AuthenticationData.userData,
});

export default connect(mapStateToProps, { rightAction })(BondContractTransactions);